import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { fetchWasteByBranch } from "../../store/actions/dashboard";
import { currentYear } from "../../utils/tools";
import { Spinner } from "react-bootstrap";

const SELECT_STYLE = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "6px 12px",
  color: "#0B1157",
  fontSize: 14,
  cursor: "pointer",
  outline: "none",
};

const fmt = (num) => String(num).replace(".", ",");

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: "10px 16px",
      boxShadow: "0 4px 16px rgba(11,17,87,0.10)",
      color: "#0B1157",
      fontSize: 13,
    }}>
      <p style={{ margin: 0, fontWeight: 700, marginBottom: 6 }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmt(entry.value)} t</strong>
        </p>
      ))}
    </div>
  );
};

const YEAR = currentYear();

const BranchCompareChart = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branch.data);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(YEAR);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchWasteByBranch(year))
      .unwrap()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, year]);

  const years = Array.from({ length: 5 }, (_, i) => YEAR - i);
  const isEmpty = !loading && data.length === 0;

  if (!loading && branches.length < 2) return null;

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      padding: "24px 24px 16px",
      boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
      height: "100%",
    }}>
      <div className="mb-4">
        <h6 style={{ color: "#0B1157", fontWeight: 700, margin: 0, fontSize: 16 }}>
          Pohyby podľa pobočiek
        </h6>
        <p style={{ color: "#718094", fontSize: 13, margin: "2px 0 12px" }}>
          Porovnanie príjmu a výdaja medzi pobočkami
        </p>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={SELECT_STYLE}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280 }}>
          <Spinner animation="border" style={{ color: "#0B1157" }} />
        </div>
      ) : isEmpty ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280, color: "#718094", fontSize: 14 }}>
          Pre zvolené obdobie nie sú žiadne dáta.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#718094", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#718094", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              unit=" t"
              width={55}
              tickFormatter={fmt}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f4f5fe" }} />
            <Legend wrapperStyle={{ fontSize: 13, color: "#718094", paddingTop: 12 }} />
            <Bar dataKey="income" name="Príjem" fill="#0B1157" radius={[4, 4, 0, 0]} maxBarSize={40} />
            <Bar dataKey="disposal" name="Výdaj" fill="#4DFFFF" radius={[4, 4, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default BranchCompareChart;
